import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import { useTranslation } from "react-i18next";
import { Button, Col, Row } from "react-bootstrap";
import { useUserQuery } from "../../graphql/menu.hooks";
import { useDuplicatePageMutation } from "../../graphql/page.hooks";
import { useNavigate } from "react-router-dom";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { useFolderDuplicateMutation } from "../../graphql/folder.hooks";
import { FREE_LIMIT } from "../../utils";
import { isUpgradeShow } from "../../graphql/state";
import ReactSelect from "../../components/inputs/select";
import { SelectOption } from "../../types";
import { useGetOptions } from "../../hooks/useGetOptions";

interface CopyFormProps {
  closeModal: () => void;
  pageId?: number | string;
  folderItemId?: number | string;
  type?: string;
}

const CopyForm: React.FC<CopyFormProps> = ({
  closeModal,
  pageId,
  folderItemId,
  type,
}) => {
  const lng = useGetUrlLocale();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: user, loading: userLoading } = useUserQuery();
  const [duplicatePage] = useDuplicatePageMutation();
  const [duplicateFolder] = useFolderDuplicateMutation();

  const [folders, setFolders] = useState([]);
  const [projectId, setProjectId] = useState<number | string>("");
  const { domainOptions, folderOptions, accessLoading } = useGetOptions({
    projectId: +projectId,
    setFolders,
  });

  const domains = useMemo(() => user?.user?.domains || [], [user]);
  const isPremium = user?.user.activePremium;

  useEffect(() => {
    if (domains.length > 0) {
      setFolders(domains[0]?.folders || []);
    }
  }, [domains]);

  const handleDuplicate = async (
    folderId: string | number,
    domainId: number
  ) => {
    try {
      if (type === "folder") {
        const { data } = await duplicateFolder({
          variables: {
            folderId: +folderItemId,
            newFolderId: +folderId || null,
            domainId: +domainId,
          },
        });
        if (data?.folderDuplicate && folderId) {
          navigate(`${lng}/projects/${domainId}/folder/${folderId}`);
        } else {
          navigate(`${lng}/projects/${domainId}`);
        }
      } else if (type === "page") {
        if (user) {
          if (
            user?.user?.countMyPages >= FREE_LIMIT.PAGE &&
            !user?.user.admin &&
            !isPremium
          ) {
            isUpgradeShow(true);
            return;
          }
        }
        const { data } = await duplicatePage({
          variables: {
            id: +pageId!,
            duplicateFile: true,
            folderId: +folderId || null,
            domainId,
          },
        });
        if (data?.duplicatePage && folderId) {
          navigate(`${lng}/projects/${domainId}/folder/${folderId}`);
        } else {
          navigate(`${lng}/projects/${domainId}`);
        }
      }
    } catch (error) {
      console.error("Error duplicating:", error);
    }
  };

  return (
    <Formik
      initialValues={{
        projectId: domains.length > 0 ? domains[0].id : "",
        folderId: "",
      }}
      onSubmit={async (values, { setSubmitting }) => {
        const folderId = values.folderId;
        const projectId = +values.projectId;
        if (!projectId) {
          console.error("Missing projectId in form submission.");
          setSubmitting(false);
          return;
        }

        try {
          await handleDuplicate(folderId, projectId);
          closeModal();
        } catch (error) {
          console.error("Error during duplication:", error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, setFieldValue, values }) => (
        <Form>
          <Field name="projectId">
            {() => (
              <ReactSelect
                className="mb-3"
                options={domainOptions}
                value={domainOptions.find(
                  (domain) => domain.value === +values.projectId
                )}
                onChange={(option: SelectOption) => {
                  setFieldValue("projectId", option.value);
                  setProjectId(option.value);
                }}
                placeholder="Select Project"
                isLoading={userLoading || accessLoading}
                label={t("page.project")}
              />
            )}
          </Field>
          <Field name="folderId">
            {() => (
              <ReactSelect
                className="mb-3 position-relative"
                options={folderOptions}
                value={folderOptions?.find(
                  (domain) => domain.value === +values.folderId
                )}
                onChange={(option: SelectOption) =>
                  setFieldValue("folderId", option.value)
                }
                placeholder="Select Folder"
                isLoading={userLoading}
                isDisabled={!folders?.length}
                label={t("page.folder")}
              />
            )}
          </Field>

          <Row className="gx-2 mt-4">
            <Col lg={6}>
              <Button
                variant="outline-dark"
                onClick={closeModal}
                className="w-100 fw-normal mb-4 mb-lg-0 dark-hover"
              >
                {t("projects.deleteCancel")}
              </Button>
            </Col>
            <Col lg={6}>
              <Button
                variant="primary"
                className="w-100 text-white"
                type="button"
                onClick={() => handleSubmit()}
                disabled={!values.projectId}
              >
                {t("copyModal.copy")}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default CopyForm;
